import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, Trophy, Plus, Feather, ArrowRight, Images, Play, Mic, Video } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { useChaptersData } from '../hooks/useChaptersData';
import { getUser } from '../utils/secureStorage';
import { getMediaServeUrl } from '../config/mediaAPI';
import { focalPosition } from '../utils/mediaLayout';
import { axiosInstance } from '../services/api';
import { StoryCard } from '../components/dashboard/FeedTimeline';
import { lifeEventAPI } from '../config/lifeEventAPI';
import ChaptersOnboarding from '../components/chapters/ChaptersOnboarding';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const CH_COLORS = ['#6B8E23', '#8B7355', '#556B2F', '#7B6B8E', '#8E6B23'];
const chColor = (i) => CH_COLORS[(i - 1) % CH_COLORS.length];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function resolveThumbUrl(m) {
  if (!m) return '';
  if (m.file_url && m.file_url.startsWith('http')) return m.file_url;
  if (m.url && m.url.startsWith('http')) return m.url;
  if (m.url && m.url.startsWith('/')) return `${API_BASE}${m.url}`;
  if (m.id) return getMediaServeUrl(m.id);
  return '';
}

/* ─── Book Header ─── */
function BookHeader({ profile, totalChapters, totalStories, totalMemories }) {
  const birthInfo = profile?.birth_year
    ? `Born ${profile.birth_month ? new Date(2000, profile.birth_month - 1).toLocaleString('default', { month: 'long' }) + ' ' : ''}${profile.birth_day ? profile.birth_day + ', ' : ''}${profile.birth_year}`
    : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28 }}>
      <div style={{
        width: 60, height: 60, borderRadius: 16, flexShrink: 0,
        background: 'linear-gradient(135deg, #8E6B23, #B08A3E)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(142,107,35,.3)',
      }}>
        <BookOpen size={28} color="#fff" />
      </div>
      <div>
        <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 32, color: '#2C2F26', margin: 0, lineHeight: 1.1 }}>
          The Life of {profile?.first_name || 'You'}
        </h1>
        <p style={{ fontFamily: FONT_B, fontSize: 15, color: '#828A76', marginTop: 4 }}>
          {birthInfo}{birthInfo && ' · '}{totalChapters} chapters · {totalStories} stories · {totalMemories} memories
        </p>
      </div>
    </div>
  );
}

/* ─── Most Recent Card ─── */
function MostRecentCard({ stories, navigate }) {
  if (!stories || stories.length === 0) return null;
  const latest = stories[0];
  return (
    <div style={{
      background: '#fff', border: '1px solid #EDEBDD', borderRadius: 18, padding: '18px 22px',
      display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28,
      boxShadow: '0 6px 24px rgba(54,60,40,.08), 0 2px 6px rgba(54,60,40,.05)',
      cursor: 'pointer',
    }} onClick={() => navigate(`/stories/${latest.id}`)}>
      <div style={{ width: 88, height: 88, borderRadius: 12, background: '#EFEEE5', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
        {latest.thumbnailUrl ? (
          <img src={latest.thumbnailUrl.startsWith('/') ? `${API_BASE}${latest.thumbnailUrl}` : latest.thumbnailUrl} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 35%', imageOrientation: 'from-image' }} loading="lazy" decoding="async" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#EAF0DE,#DCE6C8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={24} color="#556B2F" />
          </div>
        )}
        {latest.hasVideo && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play size={14} color="#2C2F26" style={{ marginLeft: 2 }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#C65D00', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 3, fontFamily: FONT_B }}>MOST RECENT</div>
        <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{latest.title || 'Untitled'}</div>
        <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B, marginTop: 2 }}>Added {latest.date ? new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
      </div>
      <button style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>
        Open story
      </button>
    </div>
  );
}

/* ─── Capped Preview (max 4 thumbs) ─── */
function CappedPreview({ chapter, navigate }) {
  const previews = chapter.memories_preview?.slice(0, 4) || [];
  if (previews.length === 0) return null;
  const total = (chapter.memory_count || 0) + (chapter.milestone_count || 0);
  const remaining = total - 3;

  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      {previews.slice(0, remaining > 0 ? 3 : 4).map((item, i) => {
        const thumbUrl = resolveThumbUrl({ url: item.thumbnail_url || item.cover_url });
        const hasThumb = !!(item.thumbnail_url || item.cover_url);
        return (
          <div key={item.id || i} style={{ flex: 1, height: 118, borderRadius: 11, overflow: 'hidden', background: '#EFEEE5', position: 'relative' }}>
            {hasThumb ? (
              <img src={thumbUrl} alt={item.title || ''} style={{
                width: '100%', height: '100%', objectFit: 'cover',
                objectPosition: '50% 35%', display: 'block', imageOrientation: 'from-image',
              }} loading="lazy" decoding="async" />
            ) : (
              <div style={{
                width: '100%', height: '100%',
                background: item.itemType === 'milestone' ? 'linear-gradient(135deg,#FFF7E0,#F5E8C0)' : 'linear-gradient(135deg,#EAF0DE,#DCE6C8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10,
              }}>
                <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontSize: 13, color: '#5A6150', textAlign: 'center', lineHeight: 1.3, margin: 0 }}>
                  {(item.title || '').slice(0, 40)}
                </p>
              </div>
            )}
          </div>
        );
      })}
      {remaining > 0 && (
        <div
          onClick={e => { e.stopPropagation(); navigate(`/my-life/chapters/${chapter.id}`); }}
          style={{
            flex: 1, height: 118, borderRadius: 11, background: 'rgba(28,32,22,.55)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#fff' }}>+{remaining}</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,.8)', fontFamily: FONT_B, marginTop: 2 }}>View all</span>
        </div>
      )}
    </div>
  );
}

/* ─── Latest Post Card (fetches + renders Dashboard StoryCard) ─── */
function LatestPostCard({ lifeEventId, navigate }) {
  const [storyData, setStoryData] = useState(null);

  useEffect(() => {
    if (!lifeEventId) return;
    lifeEventAPI.getEvent(lifeEventId)
      .then(s => {
        let media = (s.media_files || []).filter(m => m.media_type === 'image' || !m.media_type);
        if (media.length === 0 && s.thumbnailUrl) {
          const thumbUrl = s.thumbnailUrl.startsWith('/') ? `${API_BASE}${s.thumbnailUrl}` : s.thumbnailUrl;
          media = [{ id: 'thumb', url: thumbUrl, media_type: 'image' }];
        }
        // Resolve image URLs
        media = media.map(m => ({
          ...m,
          url: m.file_url || (m.url && m.url.startsWith('/') ? `${API_BASE}${m.url}` : m.url) || (m.id ? getMediaServeUrl(m.id) : ''),
        }));
        const authorName = s.author_name || `${s.user_first_name || ''}`.trim() || 'Someone';
        setStoryData({
          id: s.id,
          title: s.title || 'Untitled',
          story: s.story || '',
          category: s.event_data?.story_type || s.story_type || 'family_memory',
          date: s.date || s.created_at,
          location: s.location || '',
          author: {
            name: authorName,
            initial: authorName.charAt(0).toUpperCase(),
            color: '#3E4F22',
          },
          media,
          loveCount: 0,
          viewerSentLove: false,
          commentCount: 0,
        });
      })
      .catch(() => {});
  }, [lifeEventId]);

  if (!storyData) return null;

  return (
    <StoryCard story={storyData} onClick={() => navigate(`/stories/${storyData.id}`)} />
  );
}

/* ─── Chapter Station ─── */
function ChapterStation({ chapter, index, navigate }) {
  const color = chColor(index);
  const totalItems = (chapter.memory_count || 0) + (chapter.milestone_count || 0);
  const isEmpty = totalItems === 0;
  const isCurrent = !isEmpty && (!chapter.end_year || chapter.end_year >= new Date().getFullYear());
  const isFuture = chapter.start_year > new Date().getFullYear();
  const era = `${chapter.start_year || '?'} – ${chapter.end_year || 'Present'}`;

  return (
    <div style={{ position: 'relative', paddingLeft: 50, paddingBottom: 28 }}>
      {/* Node */}
      <div style={{
        position: 'absolute', left: 0, top: 4,
        width: 32, height: 32, borderRadius: '50%', background: color,
        color: '#fff', fontWeight: 700, fontSize: 14, fontFamily: FONT_B,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '3px solid #F5F5DC', boxShadow: '0 2px 8px rgba(0,0,0,.12)', zIndex: 1,
      }}>
        {chapter.number || index}
      </div>

      {/* Card */}
      <div style={{
        background: isEmpty ? '#FAFAF3' : '#fff',
        border: isEmpty ? '1.5px dashed #D8D3BE' : '1px solid #EDEBDD',
        borderRadius: 16, padding: '20px 22px',
        boxShadow: isEmpty ? 'none' : '0 6px 24px rgba(54,60,40,.08), 0 2px 6px rgba(54,60,40,.05)',
        opacity: isFuture ? 0.94 : 1,
        cursor: !isEmpty ? 'pointer' : 'default',
      }} onClick={() => !isEmpty && navigate(`/my-life/chapters/${chapter.id}`)}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, color: '#2C2F26', margin: 0, flex: 1 }}>
            "{chapter.title}"
          </h3>
          {isCurrent && !isEmpty && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#556B2F', background: '#F0F5E8', padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap', fontFamily: FONT_B }}>Current chapter</span>
          )}
          {isFuture && (
            <span style={{ fontSize: 12, fontStyle: 'italic', color: '#828A76', fontFamily: FONT_B }}>a chapter still to come</span>
          )}
        </div>
        <div style={{ fontSize: 14, color: '#828A76', marginBottom: 12, fontFamily: FONT_B }}>{era}</div>

        {isEmpty ? (
          /* Empty / Future invitation */
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Feather size={20} color="#C65D00" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontSize: 17, color: '#5A6150', margin: '0 0 12px', lineHeight: 1.4 }}>
                {chapter.starter_prompt || 'What story lives in this chapter?'}
              </p>
              <button
                onClick={e => { e.stopPropagation(); navigate(`/tell-a-story?chapter=${chapter.id}`); }}
                style={{
                  background: isFuture ? '#fff' : '#E65100', color: isFuture ? '#5A6150' : '#fff',
                  fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 999,
                  border: isFuture ? '1px solid #E6E4D8' : 'none', cursor: 'pointer', fontFamily: FONT_B,
                }}
              >
                {isFuture ? 'Add a note' : 'Start this chapter'}
              </button>
            </div>
          </div>
        ) : (
          /* Filled chapter */
          <>
            {/* Counts */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, color: '#5A6150', fontFamily: FONT_B }}>
                <BookOpen size={15} color={color} /> <strong>{chapter.memory_count || 0}</strong> memories
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, color: '#5A6150', fontFamily: FONT_B }}>
                <Trophy size={15} color={color} /> <strong>{chapter.milestone_count || 0}</strong> milestones
              </span>
              <span
                onClick={e => { e.stopPropagation(); navigate(`/my-life/chapters/${chapter.id}`); }}
                style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B }}
              >
                View all {totalItems} <ArrowRight size={14} style={{ verticalAlign: '-2px' }} />
              </span>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              <button onClick={e => { e.stopPropagation(); navigate(`/tell-a-story?storyType=family_memory&chapter=${chapter.id}`); }}
                style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14, padding: '9px 16px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, display: 'flex', alignItems: 'center', gap: 6 }}>
                <BookOpen size={15} /> Add Memory
              </button>
              <button onClick={e => { e.stopPropagation(); navigate(`/my-life/add-milestone?chapter=${chapter.id}`); }}
                style={{ background: '#fff', color: '#5A6150', fontWeight: 700, fontSize: 14, padding: '9px 16px', borderRadius: 999, border: '1px solid #E6E4D8', cursor: 'pointer', fontFamily: FONT_B, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trophy size={15} /> Add Milestone
              </button>
            </div>

            {/* Latest post (current chapter only) */}
            {(() => {
              const latestMemory = chapter.memories_preview?.[0];
              const latestEventId = latestMemory?.life_event_id;
              if (!latestEventId) return null;
              return (
                <div onClick={e => e.stopPropagation()}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#9AA088', marginBottom: 10, fontFamily: FONT_B }}>
                    Latest in this chapter
                  </div>
                  <LatestPostCard lifeEventId={latestEventId} navigate={navigate} />
                  <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <span
                      onClick={() => navigate(`/my-life/chapters/${chapter.id}`)}
                      style={{ fontSize: 15, fontWeight: 700, color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B }}
                    >
                      View all {totalItems} in this chapter <ArrowRight size={14} style={{ verticalAlign: '-2px' }} />
                    </span>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

const CHILD_COLORS = [
  { avatar: '#7B6B8E', bg: '#F5F0FA', border: '#C4B5D4' },
  { avatar: '#2A7AB2', bg: '#EDF4FB', border: '#A8CCE6' },
  { avatar: '#B25C2A', bg: '#FDF2EC', border: '#E0BDA5' },
  { avatar: '#6B8E23', bg: '#F0F5E8', border: '#C4D89E' },
];

/* ─── Main Page ─── */
export default function MyLifeRedesign() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const externalPersonId = searchParams.get('person') || null;
  const userData = getUser() || {};
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [myPhotoUrl, setMyPhotoUrl] = useState(null);

  // Use external ?person= param if present, otherwise selected child
  const personId = externalPersonId || selectedChildId;

  // Fetch minor children for the profile switcher + profile photo
  useEffect(() => {
    if (externalPersonId) return;
    axiosInstance.get('/genealogy/people/my_family_grouped/')
      .then(resp => {
        const minors = (resp.data.children || []).filter(c => c.is_minor);
        setChildren(minors);
      })
      .catch(() => setChildren([]));
    axiosInstance.get('/account/api/accounts/profile/')
      .then(res => {
        const photoUrl = res.data?.profile?.profile_photo_url;
        if (photoUrl) setMyPhotoUrl(photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`);
      })
      .catch(() => {});
  }, [externalPersonId]);

  const {
    profile, chapters, legacyStats, stories: personStories,
    hasCompletedOnboarding, loading, error,
  } = useChaptersData(personId);

  if (loading) {
    return (
      <HubLayout activeView="life">
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '32px 32px 80px', textAlign: 'center', color: '#828A76' }}>
          Loading your story...
        </div>
      </HubLayout>
    );
  }

  // Show onboarding (DOB + chapter generation) for self view if not completed
  if (!personId && !hasCompletedOnboarding) {
    return <ChaptersOnboarding userName={profile?.first_name || userData.first_name || 'Friend'} birthYear={profile?.birth_year || null} />;
  }

  const totalChapters = chapters.length;
  const totalMemories = chapters.reduce((s, c) => s + (c.memory_count || 0), 0);
  const totalMilestones = chapters.reduce((s, c) => s + (c.milestone_count || 0), 0);
  const totalStories = personStories?.length || 0;

  return (
    <HubLayout activeView="life">
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* Profile switcher — shown when user has minor children */}
        {!externalPersonId && children.length > 0 && (
          <div style={{
            display: 'flex', gap: 12, padding: '0 0 24px',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          }}>
            {/* Self */}
            <button
              onClick={() => setSelectedChildId(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 18px', borderRadius: 14,
                border: `2.5px solid ${!selectedChildId ? '#556B2F' : '#E6E4D8'}`,
                background: !selectedChildId ? '#F0F5E8' : '#fff',
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: '#556B2F', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, overflow: 'hidden',
              }}>
                {myPhotoUrl ? (
                  <img src={myPhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  (userData.first_name || 'Y').charAt(0).toUpperCase()
                )}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2C2F26', fontFamily: FONT_B }}>{userData.first_name || 'Me'}</div>
                <div style={{ fontSize: 12, color: '#828A76', fontFamily: FONT_B }}>My Story</div>
              </div>
            </button>

            {/* Minor children */}
            {children.map((child, idx) => {
              const color = CHILD_COLORS[idx % CHILD_COLORS.length];
              const isActive = selectedChildId === child.id;
              return (
                <button
                  key={child.id}
                  onClick={() => setSelectedChildId(child.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 14,
                    border: `2.5px solid ${isActive ? color.border : '#E6E4D8'}`,
                    background: isActive ? color.bg : '#fff',
                    cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: color.avatar, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700,
                  }}>
                    {(child.first_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2C2F26', fontFamily: FONT_B }}>{child.first_name}</div>
                    <div style={{ fontSize: 12, color: '#828A76', fontFamily: FONT_B }}>{child.age != null ? `Age ${child.age}` : 'Child'}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Book header */}
        <BookHeader profile={profile} totalChapters={totalChapters} totalStories={totalStories} totalMemories={totalMemories} />

        {/* Most recent */}
        <MostRecentCard stories={personStories} navigate={navigate} />

        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 23, color: '#2C2F26', margin: 0 }}>
            {profile?.first_name || 'Your'}'s life in chapters
          </h2>
          <button onClick={() => navigate('/tell-a-story')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14,
            padding: '9px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B,
          }}>
            <Plus size={15} /> Add Story
          </button>
        </div>

        {/* Timeline */}
        <div style={{
          borderLeft: '3px solid transparent',
          borderImage: `linear-gradient(${CH_COLORS.join(',')}) 1`,
          marginLeft: 15, paddingTop: 8,
        }}>
          {chapters.map((ch, i) => (
            <ChapterStation key={ch.id} chapter={ch} index={ch.number || i + 1} navigate={navigate} />
          ))}
        </div>

        {/* Add chapter */}
        <button
          onClick={() => navigate('/my-life/add-chapter')}
          style={{
            width: '100%', padding: '18px 24px', background: 'transparent',
            border: '2px dashed #D4D0C0', borderRadius: 16, cursor: 'pointer',
            fontFamily: FONT_B, fontWeight: 700, fontSize: 16, color: '#556B2F',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 8,
          }}
        >
          <Plus size={18} /> Add a new chapter
        </button>
      </div>

      <style>{`@media (prefers-reduced-motion: reduce) { * { transition: none !important; } }`}</style>
    </HubLayout>
  );
}
